import { writable, derived, get } from 'svelte/store';
import { selectedSite } from './selectionStore';
import { isAuthenticated } from '../lib/auth.js';

// Store for message counts by site
const messageCounts = writable({});

// Store for tracking when counts were last fetched
const lastFetchTimes = writable({});

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// API base URL
const API_BASE_URL = '/api/proxy';

/**
 * Fetch unread message count for a specific site
 * @param {string} siteId - The site ID
 * @returns {Promise<number>} - The count of unread messages
 */
async function fetchUnreadMessageCount(siteId) {
  try {
    // Fetch only unread messages with minimal page size to get count
    const url = `${API_BASE_URL}/messages?page=1&pageSize=1&siteGUID=${siteId}&status=Sent`;
    
    const res = await fetch(url);
    
    if (!res.ok) {
      throw new Error('Failed to fetch message count');
    }
    
    const responseData = await res.json();
    return responseData.totalCount || 0;
  } catch (error) {
    console.error('Error fetching unread message count:', error);
    return 0;
  }
}

/**
 * Check if cache is still valid
 * @param {string} siteId - The site ID
 * @returns {boolean} - Whether cache is still valid
 */
function isCacheValid(siteId) {
  const lastFetch = $lastFetchTimes[siteId];
  if (!lastFetch) return false;
  
  return (Date.now() - lastFetch) < CACHE_DURATION;
}

/**
 * Get message count for current site with caching. Read-side cache miss
 * also triggers a fetch — gated on isAuthenticated so we don't 401 during
 * session boot.
 */
export const currentSiteMessageCount = derived(
  [selectedSite, messageCounts, lastFetchTimes, isAuthenticated],
  ([$selectedSite, $messageCounts, $lastFetchTimes, $isAuthenticated]) => {
    if (!$selectedSite) return 0;

    const siteId = $selectedSite._id;
    const lastFetch = $lastFetchTimes[siteId];
    const currentCount = $messageCounts[siteId] || 0;

    // Return cached value if available and valid
    if ($messageCounts[siteId] !== undefined && lastFetch && (Date.now() - lastFetch) < CACHE_DURATION) {
      return currentCount;
    }

    // Trigger fetch if cache is invalid AND auth is ready (skip during boot)
    if ($isAuthenticated && (!lastFetch || (Date.now() - lastFetch) >= CACHE_DURATION)) {
      fetchAndUpdateCount(siteId);
    }

    return currentCount;
  }
);

/**
 * Fetch and update message count for a site
 * @param {string} siteId - The site ID
 */
export async function fetchAndUpdateCount(siteId) {
  if (!siteId) return;
  
  const count = await fetchUnreadMessageCount(siteId);
  
  messageCounts.update(counts => ({
    ...counts,
    [siteId]: count
  }));
  
  lastFetchTimes.update(times => ({
    ...times,
    [siteId]: Date.now()
  }));
}

/**
 * Force refresh message count for current site
 */
export async function refreshCurrentSiteCount() {
  // Get the current selected site from the store
  const selectedSiteValue = get(selectedSite);
  if (selectedSiteValue) {
    await fetchAndUpdateCount(selectedSiteValue._id);
  }
}

/**
 * Force refresh message count for a specific site
 * @param {string} siteId - The site ID to refresh count for
 */
export async function forceRefreshSiteCount(siteId) {
  if (siteId) {
    // Clear the cache for this site to force a fresh fetch
    clearSiteCache(siteId);
    // Fetch and update the count
    const count = await fetchUnreadMessageCount(siteId);
    
    // Update the store directly
    messageCounts.update(counts => ({
      ...counts,
      [siteId]: count
    }));
    
    lastFetchTimes.update(times => ({
      ...times,
      [siteId]: Date.now()
    }));
  }
}

/**
 * Clear cache for a specific site
 * @param {string} siteId - The site ID
 */
export function clearSiteCache(siteId) {
  messageCounts.update(counts => {
    const newCounts = { ...counts };
    delete newCounts[siteId];
    return newCounts;
  });
  
  lastFetchTimes.update(times => {
    const newTimes = { ...times };
    delete newTimes[siteId];
    return newTimes;
  });
}

/**
 * Clear all caches
 */
export function clearAllCaches() {
  messageCounts.set({});
  lastFetchTimes.set({});
}

// Auto-fetch when EITHER the site changes OR auth becomes ready. Without
// gating on isAuthenticated, the first subscribe fires during session boot
// before the Auth0 SDK has a token — installAuthFetch then sends the call
// without a Bearer header, the backend returns 401, and the badge stays
// at 0 until the 5-minute auto-refresh below catches up. Gating here makes
// the badge populate as soon as auth flips ready (typically <1s after
// page load).
derived([selectedSite, isAuthenticated], ([$site, $authed]) => ({ $site, $authed }))
  .subscribe(({ $site, $authed }) => {
    if ($site?._id && $authed) {
      fetchAndUpdateCount($site._id);
    }
  });

// Auto-refresh every 5 minutes (skip when not authenticated to avoid the
// same tokenless-fetch 401).
if (typeof window !== 'undefined') {
  setInterval(() => {
    const selectedSiteValue = get(selectedSite);
    const authed = get(isAuthenticated);
    if (selectedSiteValue?._id && authed) {
      fetchAndUpdateCount(selectedSiteValue._id);
    }
  }, CACHE_DURATION);
}